import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { capitalizeFirstLetter } from "../../../components/utils/Date";
import { getCategoryIconComponent } from "../../settings/waste/wasteCategoryIcons";
import {
  compareParentCategories,
  compareSortingMaterials,
} from "../../settings/waste/wasteMaterialSort";

const getAuthorizedMaterialRefs = (container) => {
  const authorized =
    container?.contentCategoryJoin?.authorizedMaterial ||
    container?.authorizedMaterial ||
    [];
  return new Set(
    authorized.map((material) =>
      String(material?.value || material?.referenceName || material || "")
        .toLowerCase()
        .trim(),
    ),
  );
};

const CampaignTrackedMaterialsSection = ({
  materialList,
  trackedMaterials,
  setTrackedMaterials,
  selectedContainers = [],
}) => {
  const { t, i18n } = useTranslation();

  const collator = useMemo(
    () =>
      new Intl.Collator(i18n.language, {
        sensitivity: "base",
        numeric: true,
      }),
    [i18n.language],
  );

  const formatParentCategoryLabel = useCallback(
    (categoryKey) => {
      if (!categoryKey || typeof categoryKey !== "string") {
        return "-";
      }
      const pluralKey = `${categoryKey}_plural`;
      const pluralLabel = t(pluralKey);
      if (pluralLabel !== pluralKey) {
        return capitalizeFirstLetter(pluralLabel);
      }
      const translated = t(categoryKey);
      if (!translated || typeof translated !== "string") {
        return categoryKey;
      }
      return capitalizeFirstLetter(translated);
    },
    [t],
  );

  const categories = useMemo(() => {
    const rawCategories = materialList
      .filter((material) => material.isActiveSortingError)
      .map((material) => material.parentCategory)
      .filter(Boolean);
    return [...new Set(rawCategories)].sort((a, b) =>
      compareParentCategories(a, b, formatParentCategoryLabel, collator),
    );
  }, [materialList, collator, formatParentCategoryLabel]);

  const sortCategoryMaterials = useCallback(
    (materials) =>
      [...materials].sort((a, b) =>
        compareSortingMaterials(
          a,
          b,
          (material) => t(material.referenceName),
          collator,
        ),
      ),
    [collator, t],
  );

  const [useDefaultSortingErrors, setUseDefaultSortingErrors] = useState(false);

  const defaultSortingErrors = useMemo(() => {
    if (!selectedContainers.length) return [];

    const authorizedSets = selectedContainers.map(getAuthorizedMaterialRefs);

    return materialList
      .filter((material) => material.isActiveSortingError)
      .filter((material) => {
        const ref = String(material.referenceName || "").toLowerCase();
        return authorizedSets.some((set) => !set.has(ref));
      })
      .map((material) => material.referenceName);
  }, [materialList, selectedContainers]);

  useEffect(() => {
    if (useDefaultSortingErrors) {
      setTrackedMaterials(defaultSortingErrors);
    }
  }, [useDefaultSortingErrors, defaultSortingErrors, setTrackedMaterials]);

  useEffect(() => {
    if (selectedContainers.length === 0 && useDefaultSortingErrors) {
      setUseDefaultSortingErrors(false);
      setTrackedMaterials([]);
    }
  }, [selectedContainers.length, useDefaultSortingErrors, setTrackedMaterials]);

  const isAllSelected = materialList
    .filter((material) => material.isActiveSortingError)
    .every((material) => trackedMaterials.includes(material.referenceName));

  const handleToggleMaterial = (refName) => {
    setTrackedMaterials((prev) =>
      prev.includes(refName)
        ? prev.filter((ref) => ref !== refName)
        : [...prev, refName],
    );
  };

  const handleToggleCategory = (categoryName) => {
    const categoryMaterials = materialList
      .filter(
        (material) =>
          material.parentCategory === categoryName &&
          material.isActiveSortingError === true,
      )
      .map((material) => material.referenceName);

    const allInCategorySelected = categoryMaterials.every((ref) =>
      trackedMaterials.includes(ref),
    );

    setTrackedMaterials((prev) =>
      allInCategorySelected
        ? prev.filter((ref) => !categoryMaterials.includes(ref))
        : [...new Set([...prev, ...categoryMaterials])],
    );
  };

  const handleToggleAll = () => {
    const allActiveRefs = materialList
      .filter((material) => material.isActiveSortingError)
      .map((material) => material.referenceName);

    if (isAllSelected) {
      setTrackedMaterials([]);
    } else {
      setTrackedMaterials(allActiveRefs);
    }
  };

  const handleToggleDefaultSortingErrors = () => {
    const next = !useDefaultSortingErrors;
    setUseDefaultSortingErrors(next);
    if (!next) {
      setTrackedMaterials([]);
    }
  };

  return (
    <section className="campaign-create-materials-section">
      <div className="campaign-create-materials-section__header">
        <h2 className="campaign-create-materials-section__title">
          {t("campaign.trackedMaterialsTitle")}
        </h2>
        <div className="campaign-create-materials-section__actions">
          <label className="campaign-create-materials-section__select-all">
            <input
              type="checkbox"
              className="campaign-create-materials-section__checkbox"
              checked={isAllSelected}
              onChange={handleToggleAll}
            />
            <span>{t("campaign.selectAllCategories")}</span>
          </label>
          {selectedContainers.length > 0 ? (
            <label className="campaign-create-materials-section__select-all">
              <input
                type="checkbox"
                className="campaign-create-materials-section__checkbox"
                checked={useDefaultSortingErrors}
                onChange={handleToggleDefaultSortingErrors}
              />
              <span>
                {t("Use default sorting errors from selected streams")}
              </span>
            </label>
          ) : null}
        </div>
      </div>

      <div className="waste-flow-sorting-panel">
        {categories.map((category) => {
          const categoryMaterials = sortCategoryMaterials(
            materialList.filter(
              (item) =>
                item.parentCategory === category && item.isActiveSortingError,
            ),
          );
          const allInCategorySelected = categoryMaterials.every((material) =>
            trackedMaterials.includes(material.referenceName),
          );
          const CategoryIcon = getCategoryIconComponent(category);

          return (
            <div key={category} className="waste-flow-sorting-card">
              <div className="waste-flow-sorting-card__header">
                <div className="waste-flow-sorting-card__icon" aria-hidden>
                  <CategoryIcon />
                </div>
                <span className="waste-flow-sorting-card__title">
                  {formatParentCategoryLabel(category)}
                </span>
                <label className="campaign-create-category-select">
                  <input
                    type="checkbox"
                    className="campaign-create-materials-section__checkbox"
                    checked={allInCategorySelected}
                    onChange={() => handleToggleCategory(category)}
                  />
                  <span>{t("campaign.selectCategory")}</span>
                </label>
              </div>

              {categoryMaterials.length > 0 ? (
                <div className="waste-flow-sorting-card__materials">
                  {categoryMaterials.map((item) => (
                    <label
                      key={item._id}
                      className="waste-flow-sorting-checkbox"
                    >
                      <input
                        type="checkbox"
                        className="waste-flow-sorting-checkbox__input"
                        checked={trackedMaterials.includes(item.referenceName)}
                        onChange={() =>
                          handleToggleMaterial(item.referenceName)
                        }
                      />
                      <span className="waste-flow-sorting-checkbox__box" />
                      <span className="waste-flow-sorting-checkbox__label">
                        {t(item.referenceName)}
                      </span>
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default CampaignTrackedMaterialsSection;
